import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, Trash2, CheckCircle, Clock, Phone, Mail, Filter, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';

const STATUS_LABELS = {
  new: { label: 'Nouveau', color: 'bg-blue-100 text-blue-800 border-blue-200' },
  treated: { label: 'Traité', color: 'bg-green-100 text-green-800 border-green-200' },
  in_progress: { label: 'En cours', color: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
};

const SERVICES_MAP = {
  crevaison: 'Dépannage crevaison',
  remplacement: 'Remplacement pneumatiques',
  'ecrou-antivol': 'Extraction écrou antivol',
  'cle-perdue': 'Clé de véhicule perdue',
  batterie: 'Panne de batterie',
  flottes: 'Flottes professionnelles',
  autre: 'Autre',
};

const AdminDashboard = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const navigate = useNavigate();
  const API = '';

  const getToken = () => localStorage.getItem('admin_token');

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/contacts`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (res.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin');
        return;
      }
      const data = await res.json();
      setContacts(data);
    } catch {
      // network error
    } finally {
      setLoading(false);
    }
  }, [API, navigate]);

  useEffect(() => {
    if (!getToken()) { navigate('/admin'); return; }
    fetchContacts();
  }, [fetchContacts, navigate]);

  const updateStatus = async (id, status) => {
    await fetch(`${API}/api/admin/contacts/${id}/status`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
      body: JSON.stringify({ status }),
    });
    fetchContacts();
  };

  const deleteContact = async (id) => {
    if (!window.confirm('Supprimer cette demande ?')) return;
    await fetch(`${API}/api/admin/contacts/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    fetchContacts();
  };

  const logout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin');
  };

  const filtered = filter === 'all' ? contacts : contacts.filter(c => c.status === filter);
  const counts = {
    all: contacts.length,
    new: contacts.filter(c => c.status === 'new').length,
    in_progress: contacts.filter(c => c.status === 'in_progress').length,
    treated: contacts.filter(c => c.status === 'treated').length,
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-lg font-black tracking-tight">
              <span className="text-[#41DB23]">reco</span><span className="text-black">pneus</span>
            </span>
            <span className="text-sm text-gray-400 hidden sm:inline">— Administration</span>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={fetchContacts}
              variant="outline"
              size="sm"
              className="gap-1.5"
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Actualiser
            </Button>
            <Button
              onClick={logout}
              variant="outline"
              size="sm"
              className="gap-1.5 text-red-600 border-red-200 hover:bg-red-50"
              data-testid="logout-btn"
            >
              <LogOut className="w-3.5 h-3.5" /> Déconnexion
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {[
            { key: 'all', label: 'Total', count: counts.all, icon: Filter, color: 'text-gray-600' },
            { key: 'new', label: 'Nouveaux', count: counts.new, icon: Clock, color: 'text-blue-600' },
            { key: 'in_progress', label: 'En cours', count: counts.in_progress, icon: RefreshCw, color: 'text-yellow-600' },
            { key: 'treated', label: 'Traités', count: counts.treated, icon: CheckCircle, color: 'text-green-600' },
          ].map(({ key, label, count, icon: Icon, color }) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              className={`p-4 rounded-xl border text-left transition-all ${
                filter === key ? 'bg-white border-[#41DB23] shadow-sm' : 'bg-white border-gray-200 hover:border-gray-300'
              }`}
              data-testid={`filter-${key}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <Icon className={`w-4 h-4 ${color}`} />
                <span className="text-xs text-gray-500">{label}</span>
              </div>
              <span className="text-2xl font-bold text-gray-900">{count}</span>
            </button>
          ))}
        </div>

        {/* Contact list */}
        {loading ? (
          <div className="text-center py-12 text-gray-400">Chargement...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
            <p className="text-gray-400">Aucune demande {filter !== 'all' ? `avec le statut "${STATUS_LABELS[filter]?.label}"` : ''}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((c) => (
              <div
                key={c.id}
                className={`bg-white border rounded-xl p-4 sm:p-5 transition-all ${
                  c.status === 'new' ? 'border-blue-200' : 'border-gray-200'
                }`}
                data-testid={`contact-card-${c.id}`}
              >
                <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4">
                  {/* Main info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <h3 className="font-bold text-gray-900">{c.nom} {c.prenom}</h3>
                      <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${STATUS_LABELS[c.status]?.color || 'bg-gray-100 text-gray-600'}`}>
                        {STATUS_LABELS[c.status]?.label || c.status}
                      </span>
                      <span className="text-xs text-gray-400">{formatDate(c.created_at)}</span>
                    </div>

                    <div className="flex flex-wrap gap-3 text-sm text-gray-600 mb-2">
                      <a href={`tel:${c.telephone}`} className="flex items-center gap-1 hover:text-[#41DB23]">
                        <Phone className="w-3.5 h-3.5" /> {c.telephone}
                      </a>
                      <a href={`mailto:${c.email}`} className="flex items-center gap-1 hover:text-[#41DB23]">
                        <Mail className="w-3.5 h-3.5" /> {c.email}
                      </a>
                    </div>

                    <div className="text-xs font-medium text-[#41DB23] mb-1">
                      {SERVICES_MAP[c.service] || c.service}
                    </div>
                    <p className="text-sm text-gray-600 whitespace-pre-line">{c.message}</p>
                  </div>

                  {/* Actions */}
                  <div className="flex sm:flex-col gap-2 flex-shrink-0">
                    {c.status !== 'treated' && (
                      <Button
                        onClick={() => updateStatus(c.id, 'treated')}
                        size="sm"
                        className="bg-[#41DB23] text-white hover:bg-[#38C41E] gap-1 text-xs"
                        data-testid={`mark-treated-${c.id}`}
                      >
                        <CheckCircle className="w-3.5 h-3.5" /> Traité
                      </Button>
                    )}
                    {c.status === 'treated' && (
                      <Button
                        onClick={() => updateStatus(c.id, 'new')}
                        size="sm"
                        variant="outline"
                        className="gap-1 text-xs"
                        data-testid={`mark-new-${c.id}`}
                      >
                        <Clock className="w-3.5 h-3.5" /> Réouvrir
                      </Button>
                    )}
                    <Button
                      onClick={() => deleteContact(c.id)}
                      size="sm"
                      variant="outline"
                      className="gap-1 text-xs text-red-600 border-red-200 hover:bg-red-50"
                      data-testid={`delete-${c.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Supprimer
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
